// The amber "needs attention" strip pinned above the connection groups on
// `#s01` (design phone/tablet/desktop headers): "Ally needs login · 2 stale
// · 1 never reported · Reconnect". Reconnect always lands on `/connect`,
// which owns the per-connection reconnect flow (WS-3). Renders nothing when
// every connection is healthy and every balance is current -- an empty
// strip is never rendered as a false all-clear banner, it is simply absent.
import { Link } from 'react-router-dom';
import type { Account } from '@/api/hooks/useAccounts';
import { StatusDot } from '@/components/StatusDot';
import type { NetWorthRollup } from './netWorthRollup';

function institutionsWith(accounts: Account[], status: Account['status']): string[] {
  const names = new Set<string>();
  for (const account of accounts) {
    if (account.status === status && account.source !== 'manual') {
      names.add(account.institution_name ?? 'Unknown institution');
    }
  }
  return Array.from(names);
}

export function NeedsAttentionStrip({
  accounts,
  rollup,
  className,
}: {
  accounts: Account[];
  rollup: NetWorthRollup;
  className?: string;
}) {
  const disconnected = accounts.filter((account) => account.status_detail === 'connection_disconnected');
  const connected = accounts.filter((account) => account.status_detail !== 'connection_disconnected');
  const needsLogin = institutionsWith(connected, 'needs_login');
  const errored = institutionsWith(connected, 'error');

  const hasAttention = disconnected.length > 0 || needsLogin.length > 0 || errored.length > 0 || rollup.staleCount > 0 || rollup.neverReportedCount > 0;
  if (!hasAttention) return null;

  const parts: string[] = [];
  const disconnectedNames = institutionsWith(disconnected, 'error');
  if (disconnectedNames.length > 0) parts.push(`${disconnectedNames.join(', ')} disconnected · saved history only`);
  if (needsLogin.length > 0) parts.push(`${needsLogin.join(', ')} needs login`);
  if (errored.length > 0) parts.push(`${errored.join(', ')} sync failed`);
  if (rollup.staleCount > 0) parts.push(`${rollup.staleCount} stale`);
  if (rollup.neverReportedCount > 0) parts.push(`${rollup.neverReportedCount} never reported`);

  return (
    <div
      className={[
        'flex min-h-11 items-center gap-2 rounded-lg border border-degraded-border bg-degraded-surface px-3 py-2 text-xs text-degraded-foreground',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="needs-attention-strip"
      role="status"
    >
      <StatusDot tone="warn" shape="filled" label="Needs attention" />
      <span className="flex-1">{parts.join(' · ')}</span>
      {(needsLogin.length > 0 || errored.length > 0) && (
        <Link to="/connect" className="flex min-h-11 items-center font-semibold text-degraded-foreground">
          Reconnect
        </Link>
      )}
    </div>
  );
}

export default NeedsAttentionStrip;
